// src/components/NutritionPlanList.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { IoAdd, IoSearch, IoRestaurantOutline } from 'react-icons/io5';

const GOAL_LABELS = {
  bulk: 'Volumen',
  cut: 'Definición',
  maintain: 'Mantenimiento',
  recomp: 'Recomposición',
};

const NutritionPlanList = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      try {
        const response = await api.get('/nutrition-plans', { params: search ? { search } : {} });
        setPlans(response.data);
      } catch (err) {
        console.error('Error cargando planes de nutrición', err);
      } finally {
        setLoading(false);
      }
    };
    const timer = setTimeout(fetchPlans, 300);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Planes de Nutrición</h1>
          <p className="text-gray-400 mt-1">Crea y gestiona las plantillas de dietas para tus clientes.</p>
        </div>
        <button
          onClick={() => navigate('/nutrition-plans/create')}
          className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
        >
          <IoAdd size={20} />
          Nuevo Plan
        </button>
      </div>

      {/* Buscador */}
      <div className="relative mb-6">
        <IoSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar plan por nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-black bg-opacity-40 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-marizan-red"
        />
      </div>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div key={item} className="glass-panel rounded-xl p-5">
              <div className="skeleton h-6 w-2/3 rounded mb-3" />
              <div className="skeleton h-4 w-full rounded mb-2" />
              <div className="skeleton h-4 w-3/5 rounded" />
            </div>
          ))}
        </div>
      )}

      {!loading && plans.length === 0 && (
        <div className="text-center border-2 border-dashed border-gray-600 rounded-xl p-12">
          <IoRestaurantOutline size={48} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 text-lg">Aún no tienes planes de nutrición creados.</p>
          <button
            onClick={() => navigate('/nutrition-plans/create')}
            className="mt-4 bg-marizan-red hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            Crear mi primer plan
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <Link
            key={plan.id}
            to={`/nutrition-plans/${plan.id}`}
            className="block glass-panel rounded-xl p-5 hover:border-marizan-red hover:-translate-y-1 transition-all duration-300"
          >
            <div className="flex justify-between items-start">
              <h2 className="text-lg font-bold text-white leading-tight">{plan.name}</h2>
              {plan.goal && (
                <span className="ml-2 px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded-full whitespace-nowrap">
                  {GOAL_LABELS[plan.goal] || plan.goal}
                </span>
              )}
            </div>
            {plan.description && (
              <p className="text-gray-400 text-sm mt-2 line-clamp-2">{plan.description}</p>
            )}
            <p className="text-gray-500 text-xs mt-3">
              {plan.daily_plans?.length ?? 0} {plan.daily_plans?.length === 1 ? 'día' : 'días'}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default NutritionPlanList;
